import React from "react";
import { DossierLabel } from "../dossier/DossierLabel";
import { useRecordMedia } from "../media/PublishedMedia";

const API = process.env.REACT_APP_BACKEND_URL;
const full = (u) => (u && u.startsWith("/") ? `${API}${u}` : u);

/**
 * CaseTimeline — THE single investigative timeline (Archive Dossier
 * Standard). Replaces the years-only TimelineSection. Each event:
 * date · headline · optional explanation · evidence badge.
 */

const BADGES = {
  "PRIMARY SOURCE":     { fg: "#5FC96C", bd: "#2E7D3A" },
  "COURT RECORD":       { fg: "#5FC96C", bd: "#2E7D3A" },
  "GOVERNMENT RECORD":  { fg: "#5FC96C", bd: "#2E7D3A" },
  CORROBORATED:         { fg: "#5FC96C", bd: "#2E7D3A" },
  ACADEMIC:             { fg: "#D9B44A", bd: "#8A6D1F" },
  "CREDIBLE REPORTING": { fg: "#D9B44A", bd: "#8A6D1F" },
  DISPUTED:             { fg: "#E08B4A", bd: "#9C5A22" },
  UNVERIFIED:           { fg: "#9A948A", bd: "#4A453D" },
};

const badgeOf = (item) => {
  const raw = (item.badge || "").toUpperCase();
  if (BADGES[raw]) return raw;
  if ((item.confidence || "").toLowerCase() === "corroborated") return "CORROBORATED";
  return null;
};

export const CaseTimeline = ({ topic, editorialModules }) => {
  const v2 = (editorialModules?.timeline || []).filter(Boolean);
  const legacy = (topic?.timeline || []).filter(Boolean);
  const items = v2.length ? v2 : legacy;
  const media = useRecordMedia("dossier", topic?.slug);
  const timelineMedia = (media || []).filter((m) => m.role === "timeline");
  const mediaForYear = (when) => {
    const m = String(when).match(/(1[89]\d{2}|20\d{2})/);
    if (!m) return null;
    return timelineMedia.find((tm) => (tm.label || "").includes(m[1])
      || (tm.evidence_date || "") === m[1]) || null;
  };
  if (items.length === 0) return null;

  return (
    <section className="mt-12" data-testid="dossier-timeline-section">
      <DossierLabel as="h2" className="mt-0">Case Timeline</DossierLabel>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065] mt-2 mb-6">
        Reconstructed from the evidence record
      </div>
      <ol className="border-l ml-1" style={{ borderColor: "#2D2A26" }} data-testid="dossier-timeline-list">
        {items.map((item, i) => {
          const when = item.date || item.year || item.when || "";
          const headline = item.headline || item.label || item.event || item.text ||
            (typeof item === "string" ? item : "");
          const body = item.body || item.explanation || null;
          const badge = badgeOf(item);
          const tone = badge ? BADGES[badge] : null;
          const evidence = mediaForYear(when);
          return (
            <li key={i} className="relative pl-6 md:pl-8 pb-7 last:pb-0">
              <span
                className="absolute -left-[4px] top-[6px] w-[8px] h-[8px] rotate-45"
                style={{ background: i === 0 || i === items.length - 1 ? "#D92D20" : "#3A3630" }}
              />
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
                <span className="font-mono text-[11px] tracking-[0.25em] text-[#D92D20]">{when}</span>
                {badge && (
                  <span
                    className="font-mono text-[8.5px] uppercase tracking-[0.2em] px-1.5 py-[2px]"
                    style={{ color: tone.fg, border: `1px solid ${tone.bd}`, borderRadius: 2 }}
                    data-testid={`timeline-badge-${i}`}
                  >
                    {badge}
                  </span>
                )}
              </div>
              <div className="text-[14.5px] text-[#E4DFD3] leading-snug mt-1">{headline}</div>
              {body && (
                <div className="text-[12.5px] text-[#8A8377] leading-relaxed mt-1 max-w-2xl">{body}</div>
              )}
              {evidence && (
                <figure className="mt-3 inline-block border border-[#3A3630] bg-black p-1.5 max-w-[280px]"
                        data-testid={`timeline-evidence-${i}`}
                        style={{ boxShadow: "0 6px 18px rgba(0,0,0,0.5)" }}>
                  <img src={full(evidence.urls.thumb_webp || evidence.urls.webp)}
                       alt={evidence.alt} loading="lazy" decoding="async"
                       className="w-full h-auto max-h-[180px] object-cover object-top"
                       style={{ filter: "sepia(0.15)" }} />
                  <figcaption className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#757065] mt-1.5 line-clamp-2 normal-case">
                    {evidence.caption || "Supporting evidence on file"}
                  </figcaption>
                </figure>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default CaseTimeline;
